<template>
	<div>
		<!-- Campaign Run Report Section -->
		<div class="campaigns-section mb-4">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link
							:to="run.auto_campaign && run.auto_campaign.id ? `/auto-campaigns/runs/${run.auto_campaign.id}` : '/auto-campaigns/list'"
							class="back-btn me-3"
							title="Back to Campaign Runs"
						>
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">
								<span v-if="run.auto_campaign && run.auto_campaign.name">{{ run.auto_campaign.name }} - {{ run.code }}</span>
								<span v-else>{{ run.code }}</span>
							</h2>
							<p class="section-subtitle">Campaign Run Report</p>
						</div>
					</div>
					<!-- Status Cards and Actions -->
					<div class="status-cards-container">
						<!-- Removed Status Card -->

						<!-- Total Items Card (show only when prepared) -->
						<div v-if="run.status === 'prepared'" class="status-card status-card-items-gray">
							<div class="status-card-number">{{ run.total_items }}</div>
							<div class="status-card-label">Total Items</div>
						</div>

						<!-- Show delivery metrics when not prepared -->
						<template v-else>
							<div class="status-card status-card-sent">
								<div class="status-card-number">{{ run.total_sent }}</div>
								<div class="status-card-label">Total Sent</div>
							</div>
							<div class="status-card status-card-transit">
								<div class="status-card-number">{{ run.total_in_transit }}</div>
								<div class="status-card-label">In Transit</div>
							</div>
							<div class="status-card status-card-delivered">
								<div class="status-card-number">{{ run.total_delivered }}</div>
								<div class="status-card-label">Delivered</div>
							</div>
							<div class="status-card status-card-returned">
								<div class="status-card-number">{{ run.total_returned }}</div>
								<div class="status-card-label">Returned</div>
							</div>
							<div class="status-card status-card-failed">
								<div class="status-card-number">{{ run.total_failed }}</div>
								<div class="status-card-label">Error</div>
							</div>
							<router-link
								v-if="run.response_stats"
								:to="{ name: 'autoCampaignResponseReport', params: { campaignId: run.auto_campaign_id } }"
								class="status-card status-card-visited"
								title="Unique URL / QR visits from mailed recipients"
							>
								<div class="status-card-number">{{ run.response_stats.visited }}<small> / {{ run.response_stats.mailed }}</small></div>
								<div class="status-card-label">Visited · {{ run.response_stats.response_rate }}%</div>
							</router-link>
						</template>

						<!-- Abort Button -->
						<button
							v-if="canAbortCampaign"
							class="abort-report-btn"
							@click="abortCampaign"
							:disabled="abortingCampaign"
							:class="{ loading: abortingCampaign }"
							title="Abort Campaign"
						>
							<i class="fad fa-plane-slash btn-icon" v-if="!abortingCampaign"></i>
							<i class="fad fa-spinner-third fa-spin btn-icon" v-else></i>
							<span class="btn-tooltip">{{ abortingCampaign ? 'Aborting...' : 'Abort Campaign' }}</span>
						</button>
					</div>
				</div>
			</div>

			<!-- Summary and Chart -->
			<div class="summary-section">
				<div class="row">
					<div :class="run.status === 'preparing' ? 'col-md-12' : 'col-md-4'">
						<div class="summary-card">
							<div class="summary-card-header">
								<h6 class="summary-card-title">Summary</h6>
							</div>
							<div class="summary-card-body">
								<div class="summary-item">
									<strong>Campaign:</strong>
									<span v-if="run.auto_campaign && run.auto_campaign.name">{{ run.auto_campaign.name }}</span>
									<span v-else>N/A</span>
								</div>
								<div class="summary-item">
									<strong>Run Code:</strong>
									<span class="font-monospace">{{ run.code }}</span>
								</div>
								<div v-if="run.destination_url || (run.auto_campaign && run.auto_campaign.run_destination_enabled)" class="summary-item run-destination-item">
									<strong>Destination:</strong>
									<span v-if="!editingDestination" class="run-destination-value">
										<a v-if="run.destination_url" :href="run.destination_url" target="_blank" rel="noopener" :title="run.destination_url">{{ run.destination_url }}</a>
										<span v-else class="text-muted">Campaign destination</span>
										<button type="button" class="btn btn-link btn-sm p-0 ms-2 text-muted" title="Edit run destination" @click="startEditDestination"><i class="fad fa-pen"></i></button>
									</span>
									<span v-else class="run-destination-edit">
										<input v-model="destinationDraft" type="url" class="form-control form-control-sm" placeholder="Empty = campaign destination" @keyup.enter="saveDestination" @keyup.esc="editingDestination = false" />
										<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="savingDestination" @click="saveDestination"><i :class="['fad', savingDestination ? 'fa-spinner fa-spin' : 'fa-check']"></i></button>
										<button type="button" class="btn btn-sm btn-link text-muted" @click="editingDestination = false"><i class="fad fa-times"></i></button>
									</span>
								</div>
								<div class="summary-item">
									<strong>Status:</strong>
									<span class="status-badge" :class="run.status">{{ run.status }}</span>
								</div>
								<div class="summary-item">
									<strong>Created:</strong>
									<span>{{ formatDateTime(run.created_at) }}</span>
								</div>
								<div v-if="run.started_at" class="summary-item">
									<strong>Started:</strong>
									<span>{{ formatDateTime(run.started_at) }}</span>
								</div>
								<div v-if="run.finished_at" class="summary-item">
									<strong>Finished:</strong>
									<span>{{ formatDateTime(run.finished_at) }}</span>
								</div>
								<div class="summary-item">
									<strong>Total Cost:</strong>
									<span class="text-success fw-bold">${{ run.cost }}</span>
								</div>
								<div class="summary-item">
									<strong>Type:</strong>
									<span class="badge" :class="campaign.type === 'letter' ? 'bg-primary' : 'bg-info'">
										{{ campaign.type === 'letter' ? 'Letter' : 'Postcard' }}
									</span>
								</div>

								<!-- Postcard Preview inside Summary Card -->
								<div v-if="postcardFrontUrl || postcardBackUrl" class="postcard-preview-inline">
									<div class="postcard-preview-header-inline">
										<strong>Postcard Preview:</strong>
									</div>
									<div class="postcard-preview-content-inline">
										<div class="postcard-images-row">
											<div v-if="postcardFrontUrl" class="postcard-inline-item">
												<div class="postcard-image-wrapper-inline">
													<img
														:src="postcardFrontUrl"
														alt="Postcard Front"
														class="postcard-image-inline"
														@error="handleImageError"
														@click="openImageModal(postcardFrontUrl, 'Postcard Front')"
														data-bs-toggle="modal"
														data-bs-target="#postcardModal"
													/>
												</div>
											</div>
											<div v-if="postcardBackUrl" class="postcard-inline-item">
												<div class="postcard-image-wrapper-inline">
													<img
														:src="postcardBackUrl"
														alt="Postcard Back"
														class="postcard-image-inline"
														@error="handleImageError"
														@click="openImageModal(postcardBackUrl, 'Postcard Back')"
														data-bs-toggle="modal"
														data-bs-target="#postcardModal"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="run.status !== 'preparing'" class="col-md-8">
						<div class="summary-card">
							<div class="summary-card-header">
								<h6 class="summary-card-title">Campaign Items Map</h6>
								<div class="map-legend">
									<div class="legend-item">
										<span class="legend-color prepared"></span>
										<span>Prepared</span>
									</div>
									<div class="legend-item">
										<span class="legend-color sent"></span>
										<span>Sent</span>
									</div>
									<div class="legend-item">
										<span class="legend-color in_transit"></span>
										<span>In Transit</span>
									</div>
									<div class="legend-item">
										<span class="legend-color delivered"></span>
										<span>Delivered</span>
									</div>
									<div class="legend-item">
										<span class="legend-color returned"></span>
										<span>Returned</span>
									</div>
									<div class="legend-item">
										<span class="legend-color error"></span>
										<span>Error</span>
									</div>
								</div>
							</div>
							<div class="summary-card-body map-container">
								<GmapMap
									:center="mapCenter"
									:zoom="mapZoom"
									map-type-id="roadmap"
									style="width: 100%; height: 100%; min-height: 400px;"
									:options="{
										zoomControl: true,
										mapTypeControl: false,
										scaleControl: true,
										streetViewControl: false,
										rotateControl: false,
										fullscreenControl: true,
										disableDefaultUI: false,
									}"
								>
									<GmapMarker
										v-for="(marker, index) in markers"
										:key="index"
										:position="marker.position"
										:title="marker.title"
										:icon="marker.icon"
										@click="showMarkerInfo(marker)"
									/>
								</GmapMap>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Secondary Tabs -->
			<div class="secondary-tabs-container">
				<div class="secondary-tabs">
					<button class="secondary-tab" :class="{ active: statusFilter === '' }" @click="setStatusFilter('')">
						<span class="secondary-tab-text">All</span>
						<span class="secondary-tab-badge">{{ items.length }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusFilter === 'sent' }" @click="setStatusFilter('sent')">
						<span class="secondary-tab-text">Sent</span>
						<span class="secondary-tab-badge">{{ getStatusCount('sent') }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusFilter === 'in_transit' }" @click="setStatusFilter('in_transit')">
						<span class="secondary-tab-text">In Transit</span>
						<span class="secondary-tab-badge">{{ getStatusCount('in_transit') }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusFilter === 'delivered' }" @click="setStatusFilter('delivered')">
						<span class="secondary-tab-text">Delivered</span>
						<span class="secondary-tab-badge">{{ getStatusCount('delivered') }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusFilter === 'returned' }" @click="setStatusFilter('returned')">
						<span class="secondary-tab-text">Returned</span>
						<span class="secondary-tab-badge">{{ getStatusCount('returned') }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusFilter === 'error' }" @click="setStatusFilter('error')">
						<span class="secondary-tab-text">Error</span>
						<span class="secondary-tab-badge">{{ getStatusCount('error') }}</span>
					</button>
				</div>

				<div class="table-tools">
					<!-- Delivery status check -->
					<div class="delivery-check">
						<button
							class="delivery-check-btn"
							:disabled="checkingDeliveries || !canCheckDeliveries"
							:title="canCheckDeliveries ? 'Pull delivery status from LOB' : 'Nothing left to track for this run'"
							@click="checkDeliveries"
						>
							<i class="fad fa-spinner-third fa-spin" v-if="checkingDeliveries"></i>
							<i class="fad fa-truck" v-else></i>
							<span v-if="!checkingDeliveries">Check delivery</span>
							<span v-else>{{ checkProgress || 'Checking…' }}</span>
						</button>
						<small v-if="run.deliveries_checked_at" class="delivery-check-meta">
							Last checked: {{ formatDateTime(run.deliveries_checked_at) }}
						</small>
					</div>

					<!-- Search -->
					<div class="search-input-wrapper">
						<i class="fad fa-search search-icon"></i>
						<input type="text" class="search-input" placeholder="Search municipalities" v-model="searchTerm" @input="filterItems" />
					</div>
				</div>
			</div>

			<!-- Delivery Items Table -->
			<div class="campaigns-table">
				<!-- Table Header -->
				<div class="table-header">
					<div class="header-cell municipality-col">Municipality</div>
					<div class="header-cell address-col">Address</div>
					<div class="header-cell status-col">Status</div>
					<div class="header-cell delivered-col">Delivered Time</div>
					<div class="header-cell unique-url-col">Unique URL</div>
					<div class="header-cell error-col">Error</div>
					<div class="header-cell actions-col"></div>
				</div>

				<!-- Table Rows -->
				<div class="table-row" v-for="item in paginatedItems" :key="item.id">
					<div class="table-cell municipality-cell">
						<div class="municipality-info">
							<div class="municipality-avatar">
								{{ getMunicipalityInitials(item.name) }}
							</div>
							<div class="municipality-details">
								<div class="municipality-name">{{ item.name }}</div>
								<div class="municipality-state">{{ item.state }}</div>
							</div>
						</div>
					</div>
					<div class="table-cell address-cell">
						<div class="address-info">
							<div class="address-line">{{ item.address }}</div>
							<div class="address-details">{{ item.city }}, {{ item.state }} {{ item.zip }}</div>
						</div>
					</div>
					<div class="table-cell status-cell">
						<span class="status-badge" :class="item.status">
							{{ item.status }}
						</span>
					</div>
					<div class="table-cell delivered-cell">
						<span
							v-if="item.delivered_at"
							class="time-text"
							:class="{ estimated: item.delivery_assumed }"
							:title="item.delivery_assumed ? 'Estimated — LOB never reported a delivery event, taken from the expected delivery date' : ''"
						>
							<span v-if="item.delivery_assumed" class="estimated-mark">~</span>{{ formatDeliveredAt(item) }}
						</span>
						<span v-else class="no-data">-</span>
					</div>
					<div class="table-cell unique-url-cell">
						<span v-if="item.unique_url" class="unique-url-link" :title="item.unique_url">{{ item.unique_url }}</span>
						<small v-if="item.unique_url_visited_at" class="unique-url-visited"><i class="fad fa-check-circle"></i> {{ formatDateTime(item.unique_url_visited_at) }}<span v-if="item.unique_url_visits > 1" class="unique-url-channel" :title="`Last visit ${formatDateTime(item.unique_url_last_visited_at)}`">{{ item.unique_url_visits }}×</span><span v-if="item.unique_url_visit_info && item.unique_url_visit_info.channel" class="unique-url-channel">{{ item.unique_url_visit_info.channel === 'qr' ? 'QR scan' : 'Typed link' }}</span></small>
						<span v-else-if="item.unique_url" class="unique-url-pending">Not visited</span>
						<span v-else class="no-data">-</span>
					</div>
					<div class="table-cell error-cell">
						<span v-if="item.error" class="error-text" :title="item.error">{{ truncateError(item.error) }}</span>
						<span v-else class="no-data">-</span>
					</div>
					<div class="table-cell actions-cell">
						<a
							v-if="item.lob_url"
							:href="item.lob_url"
							target="_blank"
							rel="noopener"
							class="action-icon-btn"
							:title="item.lob_id"
						>
							<i class="fad fa-eye"></i>
						</a>
						<button class="action-icon-btn" title="Create LOB Test Preview" @click="openMunicipalityTestPreview(item)">
							<i class="fad fa-flask"></i>
						</button>
						<router-link
							:to="{
								name: 'autoCampaignMunicipalityReport',
								params: {
									campaignId: runData.campaign_id,
									runId: runData.run_id,
									municipalityId: item.id,
								},
							}"
							class="action-icon-btn"
							title="View Municipality Report"
						>
							<i class="fad fa-file-alt"></i>
						</router-link>
					</div>
				</div>
			</div>

			<!-- Export Button -->
			<div class="export-button-container">
				<button class="export-btn" @click="exportRunItems" :disabled="items.length === 0">
					<i class="fad fa-download"></i>
					Export
				</button>
			</div>

			<!-- Empty State -->
			<div v-if="filteredItems.length === 0" class="text-center py-5">
				<div class="text-muted">
					<i class="fad fa-search" style="font-size: 2rem; margin-bottom: 8px;"></i>
					<p class="mb-0">No delivery items found</p>
				</div>
			</div>
		</div>

		<!-- Pagination -->
		<div v-if="pagination && pagination.last_page > 1" class="cl-pagination">
			<div class="cl-pagination__info">
				Showing <strong>{{ pagination.from }}</strong>–<strong>{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
			</div>
			<div class="cl-pagination__controls">
				<button class="cl-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
					<i class="fas fa-chevron-left"></i>
					<span>Prev</span>
				</button>
				<span class="cl-page-current">
					Page {{ pagination.current_page }} <span class="cl-page-current__sep">/</span> {{ pagination.last_page }}
				</span>
				<button class="cl-page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
					<span>Next</span>
					<i class="fas fa-chevron-right"></i>
				</button>
			</div>
		</div>

		<!-- Status Info -->
		<div v-if="items.length > 0" class="status-info-section">
			<div class="status-info-title">Status Definitions:</div>
			<div class="status-info-items">
				<div class="status-info-item">
					<span class="status-badge prepared">prepared</span>
					<span class="status-info-text">Item is ready but not handed to LOB yet</span>
				</div>
				<div class="status-info-item">
					<span class="status-badge sent">sent</span>
					<span class="status-info-text">Accepted by LOB, not in the mail stream yet</span>
				</div>
				<div class="status-info-item">
					<span class="status-badge in_transit">in transit</span>
					<span class="status-info-text">Mailed and moving through USPS</span>
				</div>
				<div class="status-info-item">
					<span class="status-badge delivered">delivered</span>
					<span class="status-info-text">Delivered to the recipient (~ means estimated)</span>
				</div>
				<div class="status-info-item">
					<span class="status-badge returned">returned</span>
					<span class="status-info-text">Returned to sender — address is likely bad</span>
				</div>
				<div class="status-info-item">
					<span class="status-badge error">error</span>
					<span class="status-info-text">LOB rejected the piece, nothing was mailed</span>
				</div>
			</div>
		</div>

		<!-- Bootstrap Modal for Postcard Preview -->
		<div class="modal fade" id="postcardModal" tabindex="-1" aria-labelledby="postcardModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="postcardModalLabel">{{ modalImageTitle }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body text-center">
						<img :src="modalImageSrc" :alt="modalImageTitle" class="img-fluid postcard-modal-image" />
					</div>
				</div>
			</div>
		</div>

		<!-- Municipality LOB Test Preview Modal (static backdrop — closes only via the Close button) -->
		<div class="modal fade" id="municipalityTestModal" tabindex="-1" aria-labelledby="municipalityTestModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="municipalityTestModalLabel">
							Test Preview<span v-if="municipalityTest.itemName"> - {{ municipalityTest.itemName }}</span>
						</h5>
					</div>
					<div class="modal-body text-center">
						<div v-if="municipalityTest.loading" class="py-5">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<p class="mt-3 mb-0">Creating LOB test {{ campaign.type === 'letter' ? 'letter' : 'postcard' }} and waiting for the proof…</p>
						</div>
						<div v-else-if="municipalityTest.error" class="py-4 text-danger">
							<i class="fad fa-exclamation-triangle me-2"></i>{{ municipalityTest.error }}
						</div>
						<div v-else>
							<div v-if="municipalityTest.thumbnails.length" class="d-flex flex-wrap justify-content-center gap-3">
								<div v-for="(thumb, i) in municipalityTest.thumbnails" :key="i">
									<img :src="thumb" alt="Test preview" class="img-fluid border rounded" style="max-height: 360px" />
								</div>
							</div>
							<p v-else class="text-muted py-4">No preview was generated.</p>
							<a v-if="municipalityTest.url" :href="municipalityTest.url" target="_blank" rel="noopener" class="btn btn-link btn-sm mt-2">
								<i class="fad fa-file-pdf me-1"></i> Open full proof (PDF)
							</a>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click="closeMunicipalityTestModal">Close</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index'
import { formatUtc } from '../../utils/time'
import { gmapApi } from 'vue2-google-maps'
import { mapState } from 'vuex'
import { Modal } from 'bootstrap'

export default {
	name: 'AutoCampaignRunReport',
	data() {
		return {
			campaign: {},
			run: {},
			runData: {},
			items: [],
			filteredItems: [],
			searchTerm: '',
			statusFilter: '',
			currentPage: 1,
			perPage: 10,
			statusChart: null,
			abortingCampaign: false,
			editingDestination: false,
			destinationDraft: '',
			savingDestination: false,
			checkingDeliveries: false,
			checkProgress: null,
			// Map data
			mapCenter: { lat: 39.8283, lng: -98.5795 }, // Center of US
			mapZoom: 4,
			markers: [],
			// Modal data
			modalImageSrc: '',
			modalImageTitle: '',
			// Municipality LOB test preview
			municipalityTest: { loading: false, error: null, id: null, url: null, thumbnails: [], itemName: '' },
			municipalityTestRequestId: 0,
		}
	},
	computed: {
		...mapState(['storageUrl']),
		totalPages() {
			return Math.ceil(this.filteredItems.length / this.perPage)
		},
		paginatedItems() {
			const start = (this.currentPage - 1) * this.perPage
			const end = start + this.perPage
			return this.filteredItems.slice(start, end)
		},
		// Same shape the juniper Clients list gets from Laravel, built locally — all
		// items are already loaded here because the map and the filters need them.
		pagination() {
			const total = this.filteredItems.length
			if (!total) return null
			const from = (this.currentPage - 1) * this.perPage + 1
			return {
				from,
				to: Math.min(from + this.perPage - 1, total),
				total,
				current_page: this.currentPage,
				last_page: this.totalPages,
			}
		},
		// Only items LOB can still tell us something about are worth a check.
		canCheckDeliveries() {
			return this.items.some(item => item.lob_id && ['sent', 'in_transit'].includes(item.status))
		},
		postcardFrontUrl() {
			return this.run.postcard_front ? this.storageUrl + '/' + this.run.postcard_front : null
		},
		postcardBackUrl() {
			return this.run.postcard_back ? this.storageUrl + '/' + this.run.postcard_back : null
		},
		canAbortCampaign() {
			// Only allow abort for sending campaigns or recently completed ones
			if (this.run.status === 'sending') return true
			
			if (this.run.status === 'completed' && this.run.finished_at) {
				const finishedTime = new Date(this.run.finished_at)
				const currentTime = new Date()
				const timeDiffMinutes = (currentTime - finishedTime) / (1000 * 60)
				
				return timeDiffMinutes <= 20
			}
			
			return false
		},
		google: gmapApi,
	},
	async mounted() {
		await this.loadRun()
		await this.loadRunItems()
		// Map initialization will be handled in loadRunItems
		// Chart creation removed in favor of map
	},
	beforeDestroy() {
		if (this.statusChart) this.statusChart.destroy()
		const municipalityTestEl = document.getElementById('municipalityTestModal')
		if (municipalityTestEl) municipalityTestEl.removeEventListener('hidden.bs.modal', this.cleanupMunicipalityTest)
		this.cleanupMunicipalityTest()
		if (this.municipalityTestModalInstance) this.municipalityTestModalInstance.dispose()
	},
	methods: {
		startEditDestination() {
			this.destinationDraft = this.run.destination_url || ''
			this.editingDestination = true
		},
		async saveDestination() {
			this.savingDestination = true
			try {
				const response = await restApi.put(`/auto-campaign-runs/${this.run.id}`, { destination_url: this.destinationDraft.trim() || null })
				this.run.destination_url = response.data.destination_url
				this.editingDestination = false
			} catch (error) {
				const message = error.response?.data?.errors?.destination_url?.[0] || 'Could not save the run destination.'
				this.$swal.fire('Error', message, 'error')
			} finally {
				this.savingDestination = false
			}
		},
		async loadRun() {
			try {
				const runId = this.$route.params.runId
				const response = await restApi.get(`/auto-campaign-runs/${runId}`)
				this.run = response.data
				// The API serializes the relation as auto_campaign; run.campaign is always
				// undefined, which is why the Type badge used to read "Postcard" for letters too.
				this.campaign = this.run.auto_campaign || {}

				// Set backwards compatibility runData for existing template references
				this.runData = {
					run_id: this.run.id,
					campaign_id: this.campaign.id,
					campaign_name: this.campaign.name,
					run_date: this.formatDate(this.run.created_at),
					type: this.campaign.type,
					total_sent: this.run.total_sent,
					delivered: this.run.total_delivered,
					in_transit: this.run.total_sent - this.run.total_delivered - this.run.total_failed,
					failed: this.run.total_failed,
					total_cost: this.run.cost,
					started_at: this.run.started_at,
					completed_at: this.run.finished_at,
				}
			} catch (error) {
				console.error('Error loading run data:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to load run data',
					icon: 'error',
				})
			}
		},
		async loadRunItems() {
			try {
				const runId = this.$route.params.runId
				const response = await restApi.get(`/auto-campaign-runs/${runId}/items`)
				this.items = response.data
				this.filteredItems = [...this.items]
				// Initialize map markers after loading items
				this.initializeMapMarkers()
			} catch (error) {
				console.error('Error loading run items:', error)
				// Fallback to empty array if API fails
				this.items = []
				this.filteredItems = []
			}
		},
		filterItems() {
			this.filteredItems = this.items.filter(item => {
				const matchesSearch =
					(item.name && item.name.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
					(item.city && item.city.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
					(item.address && item.address.toLowerCase().includes(this.searchTerm.toLowerCase()))
				const matchesStatus = !this.statusFilter || item.status === this.statusFilter

				return matchesSearch && matchesStatus
			})
			this.currentPage = 1
		},
		async checkDeliveries() {
			if (this.checkingDeliveries) return

			// The API checks as much as it can inside a fixed time budget (production has
			// no way to extend a request), so keep asking until it reports nothing left.
			const MAX_ROUNDS = 30
			const totals = { checked: 0, delivered: 0, in_transit: 0, returned: 0, assumed: 0, errors: 0 }

			this.checkingDeliveries = true
			this.checkProgress = null

			try {
				const runId = this.$route.params.runId
				let remaining = 0
				let round = 0
				// Marks this click as one cycle, so each item is polled once no matter
				// how many requests it takes to get through them.
				let cycle = null

				do {
					const response = await restApi.post(`/auto-campaign-runs/${runId}/check-deliveries`, { cycle })
					const report = response.data

					cycle = report.cycle || cycle

					Object.keys(totals).forEach(key => {
						totals[key] += report[key] || 0
					})

					remaining = report.remaining || 0
					round++
					this.checkProgress = remaining ? `${totals.checked} done, ${remaining} left` : null
				} while (remaining > 0 && round < MAX_ROUNDS)

				await this.loadRun()
				await this.loadRunItems()

				// Alerts are globally configured as a 3s toast, so keep this to one line.
				const summary = [
					`${totals.checked} checked`,
					`${totals.delivered} delivered`,
					`${totals.in_transit} in transit`,
					`${totals.returned} returned`,
				]

				if (totals.errors) summary.push(`${totals.errors} failed to check`)
				if (remaining > 0) summary.push(`${remaining} left for the nightly check`)

				this.$swal.fire({
					title: 'Delivery check finished',
					text: summary.join(' · '),
					icon: 'success',
				})
			} catch (error) {
				console.error('Error checking deliveries:', error)
				this.$swal.fire({
					title: 'Delivery check failed',
					text: error.response?.data?.message || error.response?.data?.error || 'Could not reach LOB. Please try again.',
					icon: 'error',
					timer: 6000,
				})
			} finally {
				this.checkingDeliveries = false
				this.checkProgress = null
			}
		},
		setStatusFilter(status) {
			this.statusFilter = status
			this.filterItems()
		},
		getStatusCount(status) {
			return this.items.filter(item => item.status === status).length
		},
		getMunicipalityInitials(name) {
			if (!name) return 'MU'
			return name
				.split(' ')
				.map(word => word.charAt(0))
				.join('')
				.substring(0, 2)
				.toUpperCase()
		},
		getStatusBadgeClass(status) {
			const classes = {
				sent: 'bg-info',
				delivered: 'bg-success',
				in_transit: 'bg-warning text-dark',
				error: 'bg-danger',
				returned: 'bg-secondary',
			}
			return classes[status] || 'bg-secondary'
		},
		// APIs serialize naive UTC; parsing with new Date() would shift these by the
		// viewer's offset (2h for us), so both go through the shared helpers.
		formatDateTime(dateString) {
			if (!dateString) return '-'
			return formatUtc(dateString, 'M/D/YYYY h:mm:ss A')
		},
		// An assumed delivery comes from LOB's expected delivery *date*, so the midnight
		// time that carries with it is noise — show the day only.
		formatDeliveredAt(item) {
			if (!item.delivered_at) return '-'

			return item.delivery_assumed ? this.formatDate(item.delivered_at) : this.formatDateTime(item.delivered_at)
		},
		// Delivered line for the map popup; kept out of the template literal so the
		// estimated case does not turn into an unreadable nested ternary.
		deliveredInfoHtml(item) {
			if (!item.delivered_at) return ''

			const time = this.formatDeliveredAt(item)

			if (item.delivery_assumed) {
				return `<p><strong>Delivered:</strong> ~${time} <em>(estimated)</em></p>`
			}

			return `<p><strong>Delivered:</strong> ${time}</p>`
		},
		formatDate(dateString) {
			if (!dateString) return '-'
			return formatUtc(dateString, 'M/D/YYYY')
		},
		changePage(page) {
			if (page >= 1 && page <= this.totalPages) {
				this.currentPage = page
			}
		},
		initializeMapMarkers() {
			if (!this.items || this.items.length === 0) {
				console.log('No items available for map markers')
				return
			}

			console.log('Items for map:', this.items.length, this.items)

			// Create markers from items with valid coordinates
			this.markers = this.items
				.filter(item => {
					// Check different possible data structures for coordinates
					let hasCoords = false

					if (item.municipality && typeof item.municipality === 'object') {
						// Municipality as relationship object
						hasCoords = (item.municipality.latitude && item.municipality.longitude) || (item.municipality.lat && item.municipality.lng)
					} else if (item.lat && item.lng) {
						// Coordinates directly on item
						hasCoords = true
					} else if (item.latitude && item.longitude) {
						// Alternative coordinate field names directly on item
						hasCoords = true
					}

					if (!hasCoords) {
						console.log('Item missing coordinates:', item.name, item)
					}
					return hasCoords
				})
				.map(item => {
					// Determine coordinate source
					let lat, lng

					if (item.municipality && typeof item.municipality === 'object') {
						// Municipality as relationship object
						lat = item.municipality.lat || item.municipality.latitude
						lng = item.municipality.lng || item.municipality.longitude
					} else {
						// Coordinates directly on item
						lat = item.lat || item.latitude
						lng = item.lng || item.longitude
					}

					const marker = {
						position: {
							lat: parseFloat(lat),
							lng: parseFloat(lng),
						},
						title: `${item.name} - ${item.status}`,
						status: item.status,
						data: item,
						icon: this.getMarkerIcon(item.status),
					}
					console.log('Created marker:', marker)
					return marker
				})

			console.log('Total markers created:', this.markers.length)

			// Adjust map center and zoom to fit all markers
			if (this.markers.length > 0) {
				this.fitMapToMarkers()
			}
		},
		getMarkerIcon(status) {
			// Status color mapping following auto campaign colors
			const statusColors = {
				prepared: '#93c5fd', // light blue
				sent: '#3b82f6', // blue
				in_transit: '#f59e0b', // orange
				delivered: '#10b981', // green
				returned: '#6b7280', // gray
				error: '#ef4444', // red
			}

			const color = statusColors[status] || '#6b7280'

			return {
				url: `data:image/svg+xml;charset=UTF-8,%3csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='12' cy='12' r='8' fill='${encodeURIComponent(
					color
				)}' stroke='%23ffffff' stroke-width='2'/%3e%3c/svg%3e`,
				scaledSize: { width: 24, height: 24 },
				anchor: { x: 12, y: 12 },
			}
		},
		fitMapToMarkers() {
			if (this.markers.length === 0) return

			// Calculate bounds of all markers
			const bounds = {
				north: Math.max(...this.markers.map(m => m.position.lat)),
				south: Math.min(...this.markers.map(m => m.position.lat)),
				east: Math.max(...this.markers.map(m => m.position.lng)),
				west: Math.min(...this.markers.map(m => m.position.lng)),
			}

			// Set center to middle of bounds
			this.mapCenter = {
				lat: (bounds.north + bounds.south) / 2,
				lng: (bounds.east + bounds.west) / 2,
			}

			// Calculate zoom level based on bounds
			const latDiff = bounds.north - bounds.south
			const lngDiff = bounds.east - bounds.west
			const maxDiff = Math.max(latDiff, lngDiff)

			if (maxDiff < 0.1) this.mapZoom = 12
			else if (maxDiff < 0.5) this.mapZoom = 10
			else if (maxDiff < 2) this.mapZoom = 8
			else if (maxDiff < 10) this.mapZoom = 6
			else this.mapZoom = 4
		},
		showMarkerInfo(marker) {
			const item = marker.data
			this.$swal.fire({
				title: item.name,
				html: `
					<div style="text-align: left;">
						<p><strong>Status:</strong> <span class="badge bg-${this.getStatusBadgeClass(item.status).replace('bg-', '')}">${item.status}</span></p>
						<p><strong>Address:</strong> ${item.address}</p>
						<p><strong>City:</strong> ${item.city}, ${item.state} ${item.zip}</p>
						${this.deliveredInfoHtml(item)}
						${item.lob_url ? `<p><a href="${item.lob_url}" target="_blank" rel="noopener">View on LOB</a></p>` : ''}
						${item.error ? `<p><strong>Error:</strong> <span style="color: #dc2626;" title="${item.error.replace(/"/g, '&quot;')}">${this.truncateError(item.error)}</span></p>` : ''}
					</div>
				`,
				showCancelButton: false,
				confirmButtonText: 'Close',
				confirmButtonColor: '#3b82f6',
			})
		},

		async exportRunItems() {
			try {
				const runId = this.$route.params.runId
				const response = await restApi.get(`/auto-campaign-runs/${runId}/items/export-csv`, {
					responseType: 'blob'
				})
				
				const url = window.URL.createObjectURL(new Blob([response.data]))
				const link = document.createElement('a')
				link.href = url
				link.setAttribute('download', `campaign_run_items_${runId}.csv`)
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (error) {
				console.error('Error exporting run items:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to export run items',
					icon: 'error',
				})
			}
		},
		handleImageError(event) {
			// Handle image loading errors gracefully
			event.target.style.display = 'none'
			const wrapper = event.target.parentElement
			if (wrapper) {
				wrapper.innerHTML = '<div class="image-error"><i class="fad fa-exclamation-triangle"></i><p>Image could not be loaded</p></div>'
			}
		},
		openImageModal(imageSrc, title) {
			// Set modal data - Bootstrap will handle showing the modal
			this.modalImageSrc = imageSrc
			this.modalImageTitle = title
		},
		ensureMunicipalityTestModal() {
			if (this.municipalityTestModalInstance) return this.municipalityTestModalInstance
			// Lazy init (element only exists once the report has rendered): static backdrop
			// so it can't be closed by clicking outside; the LOB test is deleted on close.
			const municipalityTestEl = document.getElementById('municipalityTestModal')
			this.municipalityTestModalInstance = new Modal(municipalityTestEl, { backdrop: 'static', keyboard: false })
			municipalityTestEl.addEventListener('hidden.bs.modal', this.cleanupMunicipalityTest)
			return this.municipalityTestModalInstance
		},
		async openMunicipalityTestPreview(item) {
			const requestId = ++this.municipalityTestRequestId
			this.municipalityTest = { loading: true, error: null, id: null, url: null, thumbnails: [], itemName: item.name || '' }
			this.ensureMunicipalityTestModal().show()

			try {
				const endpoint = this.campaign.type === 'letter' ? '/auto-campaign-lob/send-letter-test' : '/auto-campaign-lob/send-postcard-test'
				const response = await restApi.post(endpoint, { id: item.id })
				// LOB renders the proof thumbnails asynchronously — give it time before showing them.
				await new Promise(resolve => setTimeout(resolve, 15000))
				const data = response.data.data
				if (requestId !== this.municipalityTestRequestId) {
					// Modal was closed while waiting — delete the test we just created.
					await restApi.post('/auto-campaign-lob/delete-template-test', { id: data.id })
					return
				}
				const thumbnails =
					data.thumbnails && data.thumbnails.length
						? data.thumbnails.map(thumb => thumb.large || thumb.medium || thumb.small).filter(Boolean)
						: data.thumbnail
						? [data.thumbnail]
						: []

				this.municipalityTest = {
					loading: false,
					error: null,
					id: data.id,
					url: data.url,
					thumbnails,
					itemName: this.municipalityTest.itemName,
				}
			} catch (error) {
				if (requestId !== this.municipalityTestRequestId) return
				this.municipalityTest.loading = false
				this.municipalityTest.error = error.response?.data?.message || 'Failed to create the LOB test preview.'
			}
		},
		closeMunicipalityTestModal() {
			// Hiding triggers `hidden.bs.modal` → cleanupMunicipalityTest().
			this.municipalityTestModalInstance.hide()
		},
		async cleanupMunicipalityTest() {
			this.municipalityTestRequestId += 1
			const id = this.municipalityTest.id
			// Reset first so cleanup is idempotent and reopening starts fresh.
			this.municipalityTest = { loading: false, error: null, id: null, url: null, thumbnails: [], itemName: '' }

			if (!id) return

			try {
				// Cancel only the LOB test delivery — the run item's postcard files stay untouched.
				await restApi.post('/auto-campaign-lob/delete-template-test', { id })
			} catch (error) {
				// Best-effort cleanup; LOB also auto-expires test deliveries.
			}
		},
		truncateError(error) {
			if (!error) return ''

			// Try to parse JSON and extract meaningful message
			try {
				const errorObj = JSON.parse(error)
				let message = ''

				if (errorObj.error && errorObj.error.message) {
					message = errorObj.error.message
				} else if (errorObj.message) {
					message = errorObj.message
				} else {
					message = error
				}

				// Truncate to approximately 50 characters
				return message.length > 50 ? message.substring(0, 50) + '...' : message
			} catch (e) {
				// If not valid JSON, treat as plain text
				return error.length > 50 ? error.substring(0, 50) + '...' : error
			}
		},
		async abortCampaign() {
			try {
				// Show confirmation dialog
				const result = await this.$swal.fire({
					title: 'Are you sure?',
					text: `This will abort "${this.run.auto_campaign?.name || this.run.code}" and cancel any pending items. This action cannot be undone!`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#d33',
					cancelButtonColor: '#3085d6',
					confirmButtonText: 'Yes, abort it!',
					// Override global toast settings for this dialog
					toast: false,
					position: 'center',
					showConfirmButton: true,
					timer: null,
					timerProgressBar: false,
				})

				if (result.isConfirmed) {
					this.abortingCampaign = true

					// Call the API endpoint
					const response = await restApi.post('/auto-campaign-lob/abort-campaign', {
						campaign_run_id: this.run.id,
					})

					this.abortingCampaign = false

					// Update run status
					this.run.status = response.data.status

					// Refresh the items to show updated statuses
					await this.loadRunItems()

					// Show success message with summary
					const summary = response.data.summary
					this.$swal.fire({
						icon: 'success',
						title: 'Campaign Aborted Successfully!',
						html: `
							<div style="text-align: left; margin-top: 16px;">
								<p><strong>Summary:</strong></p>
								<ul>
									<li>Total Items: ${summary.total_items}</li>
									<li>Cancelled: ${summary.cancelled}</li>
									${summary.errors > 0 ? `<li>Errors: ${summary.errors}</li>` : ''}
								</ul>
							</div>
						`,
						timer: 5000,
						showConfirmButton: true,
					})
				}
			} catch (error) {
				this.abortingCampaign = false
				console.error('Error aborting campaign:', error)

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Abort Campaign',
					text: error.response?.data?.message || 'An error occurred while aborting the campaign.',
					confirmButtonText: 'Close',
				})
			}
		},
	},
}
</script>

<style scoped>
/* Campaigns Section */
.campaigns-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.section-header {
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Status Cards */
.status-cards-container {
	display: flex;
	gap: 12px;
	align-items: center;
	justify-content: flex-end;
}

.status-card {
	padding: 12px 16px;
	border-radius: 8px;
	text-align: center;
	min-width: 80px;
	border: 1px solid transparent;
}

/* Light color status cards */
.status-card-sent {
	background-color: #dbeafe;
	color: #1e40af;
	border-color: #bfdbfe;
}

.status-card-delivered {
	background-color: #dcfce7;
	color: #166534;
	border-color: #bbf7d0;
}

.status-card-transit {
	background-color: #fef3c7;
	color: #92400e;
	border-color: #fde68a;
}

.status-card-failed {
	background-color: #fecaca;
	color: #dc2626;
	border-color: #fca5a5;
}

.status-card-visited {
	background-color: #eef2ff;
	color: #4338ca;
	border-color: #e0e7ff;
	text-decoration: none;
}

.status-card-visited small {
	font-size: 0.6em;
	font-weight: 500;
}

.status-card-returned {
	background-color: #f3f4f6;
	color: #4b5563;
	border-color: #e5e7eb;
}

.status-card-items {
	background-color: #e0f2fe;
	color: #0369a1;
	border-color: #bae6fd;
}

.status-card-items-gray {
	background-color: #f3f4f6;
	color: #6b7280;
	border-color: #e5e7eb;
}

.status-card-status {
	background-color: #f3f4f6;
	color: #374151;
	border-color: #e5e7eb;
}

.status-card-number {
	font-size: 20px;
	font-weight: 700;
	line-height: 1;
	margin-bottom: 4px;
}

.status-card-label {
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Summary Section */
.summary-section {
	padding: 0 24px 24px 24px;
}

.summary-card {
	background: #f8f9fa;
	border-radius: 8px;
	overflow: hidden;
	height: 100%;
}

.summary-card-header {
	padding: 12px 16px;
	background: #e9ecef;
	border-bottom: 1px solid #dee2e6;
}

.summary-card-title {
	font-size: 14px;
	font-weight: 600;
	color: #495057;
	margin: 0;
}

.summary-card-body {
	padding: 16px;
}

.run-destination-value {
	min-width: 0;
	max-width: 70%;
	display: flex;
	align-items: center;
}
.run-destination-value a {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.run-destination-edit {
	display: flex;
	gap: 4px;
	width: 70%;
}
.summary-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid #e9ecef;
	font-size: 13px;
}

.summary-item:last-child {
	border-bottom: none;
}

.summary-item strong {
	color: #495057;
	font-weight: 600;
}

.card {
	border: none;
	box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Secondary Tabs Container */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 24px 24px 24px;
}

.secondary-tabs {
	display: flex;
	gap: 0;
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.secondary-tab {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;
}

.secondary-tab.active {
	background-color: #ffffff;
	color: #111827;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.secondary-tab:hover:not(.active) {
	color: #374151;
}

.secondary-tab-text {
	font-weight: 500;
}

.secondary-tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.secondary-tab.active .secondary-tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Campaigns Table */
.campaigns-table {
	border-top: 1px solid #f3f4f6;
}

.table-header {
	display: grid;
	grid-template-columns: 2fr 2fr 120px 170px 190px 1fr 110px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.table-row {
	display: grid;
	grid-template-columns: 2fr 2fr 120px 170px 190px 1fr 110px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Municipality Info */
.municipality-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.municipality-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.municipality-details {
	min-width: 0;
}

.municipality-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
}

.municipality-state {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

/* Address Info */
.address-info {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.address-line {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

.address-details {
	font-size: 12px;
	color: #6b7280;
}

/* Status Badge */
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
}

.status-badge.delivered {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.in_transit {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.failed {
	background-color: #fecaca;
	color: #dc2626;
}

.status-badge.sent {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.returned {
	background-color: #f3f4f6;
	color: #374151;
}

.status-badge.preparing {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.completed {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.prepared {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.error {
	background-color: #fecaca;
	color: #dc2626;
}

.status-badge.cancelled {
	background-color: #f3f4f6;
	color: #6b7280;
}

/* Table Cell Styles */
.time-text {
	color: #6b7280;
	font-size: 13px;
}

.time-text.estimated {
	color: #9ca3af;
	cursor: help;
}

.estimated-mark {
	margin-right: 2px;
	font-weight: 600;
}

.cost-text {
	color: #059669;
	font-weight: 500;
}

.lob-id {
	font-family: 'Courier New', monospace;
	font-size: 12px;
	color: #6b7280;
}

.error-text {
	color: #dc2626;
	font-size: 12px;
	cursor: help;
	border-bottom: 1px dotted #dc2626;
}

/* Actions */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	background: none;
	border: none;
	padding: 8px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	text-decoration: none;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
	text-decoration: none;
}

.no-data {
	color: #9ca3af;
}

/* Export Button */
.export-button-container {
	padding: 16px 24px;
	border-top: 1px solid #f3f4f6;
	display: flex;
	justify-content: flex-end;
}

.export-btn {
	background-color: #3b82f6;
	color: white;
	border: none;
	padding: 10px 20px;
	border-radius: 20px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
}

.export-btn:hover:not(:disabled) {
	background-color: #2563eb;
}

.export-btn:disabled {
	background-color: #9ca3af;
	cursor: not-allowed;
}

.export-btn i {
	font-size: 14px;
}

/* Table tools — delivery check + search, right of the status tabs */
.table-tools {
	display: flex;
	align-items: center;
	gap: 12px;
}

.delivery-check {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
}

.delivery-check-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.delivery-check-btn:hover:not(:disabled) {
	background: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
}

.delivery-check-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.delivery-check-meta {
	font-size: 11px;
	color: #9ca3af;
	white-space: nowrap;
}

/* Status Info — same shape as the juniper clients list */
.status-info-section {
	padding: 20px 24px;
	background-color: #f9fafb;
	border-top: 1px solid #f3f4f6;
}

.status-info-title {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 12px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.status-info-items {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
}

.status-info-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.status-info-text {
	font-size: 13px;
	color: #6b7280;
}

/* Pagination — clean Prev / page / Next, matching the juniper clients list */
.cl-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	border-top: 1px solid #f3f4f6;
}

.cl-pagination__info {
	font-size: 14px;
	color: #6b7280;
}

.cl-pagination__info strong {
	color: #111827;
	font-weight: 600;
}

.cl-pagination__controls {
	display: flex;
	align-items: center;
	gap: 10px;
}

.cl-page-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #e5e7eb;
	background: #fff;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cl-page-btn:hover:not(:disabled) {
	background: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
}

.cl-page-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.cl-page-btn i {
	font-size: 11px;
}

.cl-page-current {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	min-width: 92px;
	text-align: center;
}

.cl-page-current__sep {
	color: #9ca3af;
	margin: 0 2px;
}

/* Pagination */
.pagination-container {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 24px;
	padding: 16px 0;
}

.pagination-info {
	font-size: 14px;
	color: #6b7280;
	margin-right: 20px;
}

.pagination-controls {
	display: flex;
	gap: 8px;
}

.pagination-btn {
	padding: 8px 16px;
	border: 1px solid #d1d5db;
	background: white;
	border-radius: 6px;
	font-size: 14px;
	color: #374151;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
}

.pagination-btn:hover:not(:disabled) {
	background-color: #f9fafb;
	border-color: #9ca3af;
}

.pagination-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Map Styles */
.map-container {
	padding: 0 !important;
	height: 100%;
}

.map-legend {
	display: flex;
	gap: 16px;
	align-items: center;
	flex-wrap: wrap;
}

.legend-item {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #6b7280;
}

.legend-color {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 1px solid #ffffff;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.legend-color.prepared {
	background-color: #93c5fd;
}

.legend-color.sent {
	background-color: #3b82f6;
}

.legend-color.in_transit {
	background-color: #f59e0b;
}

.legend-color.delivered {
	background-color: #10b981;
}

.legend-color.returned {
	background-color: #6b7280;
}

.legend-color.error {
	background-color: #ef4444;
}

.vue-map-container .vue-map {
	border-radius: 0 !important;
}

/* Inline Postcard Preview Styles */
.postcard-preview-inline {
}

.postcard-preview-header-inline {
	margin-bottom: 12px;
	color: #495057;
	font-size: 13px;
}

.postcard-preview-content-inline {
	margin-top: 8px;
}

.postcard-images-row {
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
}

.postcard-inline-item {
	flex: 1;
	min-width: 120px;
	text-align: center;
}

.postcard-image-inline {
	max-width: 100%;
	max-height: 150px;
	width: auto;
	height: auto;
	border-radius: 4px;
	cursor: pointer;
	transition: transform 0.2s ease;
}

.postcard-image-inline:hover {
	transform: scale(1.05);
}

.summary-card-title i {
	color: #6366f1;
}

.image-error {
	padding: 40px 20px;
	color: #ef4444;
	text-align: center;
}

.image-error i {
	font-size: 24px;
	margin-bottom: 8px;
	display: block;
}

.image-error p {
	font-size: 14px;
	margin: 0;
	color: #6b7280;
}

/* Bootstrap Modal Styles for Postcard Preview */
/* Abort Button in Report */
.abort-report-btn {
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 2px solid #ef4444;
	border-radius: 50%;
	color: #ef4444;
	cursor: pointer;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.abort-report-btn:hover {
	background: #fef2f2;
	border-color: #dc2626;
	color: #dc2626;
	text-decoration: none;
}

.abort-report-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.abort-report-btn .btn-icon {
	transition: transform 0.3s ease;
	font-size: 16px;
}

.abort-report-btn:hover .btn-icon {
	transform: scale(1.1);
}

.abort-report-btn .btn-tooltip {
	position: absolute;
	right: 50px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.abort-report-btn .btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.abort-report-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

.postcard-modal-image {
	max-height: 70vh;
	width: auto;
	height: auto;
	border-radius: 8px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.unique-url-cell { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; min-width: 0; }
.unique-url-link { display: block; max-width: 175px; overflow: hidden; color: #475569; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.unique-url-visited { color: #198754; font-size: 10px; }
.unique-url-pending { color: #8a909c; font-size: 10px; }
.unique-url-channel { margin-left: 4px; padding: 0 4px; border-radius: 3px; background: #e8f3ee; font-weight: 600; }
</style>
